'use client';
/**
 * LES CHECK-LISTS DE POSTE — l'écran qui manquait (RQ, 05/10/2026).
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * « Où est-ce que je peux gérer les check-lists reliées au poste ? » — nulle
 * part. Les 4 listes et leurs 141 tâches étaient écrites en dur dans
 * `backend/src/data/checklists-postes.ts` : corriger une tâche demandait une
 * modification de code et un redéploiement, alors que la RQ avait posé pour
 * tout le reste du poste « je veux pouvoir garder la main sur le paramétrage de
 * tous les postes ». Elles sont devenues un registre, et voici son écran.
 *
 * ── ⚠️ CE QUE CET ÉCRAN NE DOIT JAMAIS LAISSER CROIRE ─────────────────────
 *
 * Cocher une case NE LAISSE AUCUNE TRACE (décision de la RQ du 26/08/2026,
 * maintenue) : les cases vivent dans le navigateur de la personne et se
 * remettent à zéro chaque jour. La grille papier reste la seule preuve
 * opposable. Une liste cochable ressemble à s'y méprendre à une preuve — la
 * réserve est donc écrite EN HAUT de l'écran, pas en note de bas de page.
 *
 * ── Ce que l'écran SIGNALE, et pourquoi ──────────────────────────────────
 *
 * Les écarts sont calculés par le serveur et affichés en premier : ce sont eux
 * qui font qu'une liste cesse de s'afficher sans que personne le sache — un
 * poste renommé, un appel vers le vide, deux listes sur le même poste. Un
 * registre qui les tairait serait pire que le fichier de code qu'il remplace.
 */
import React from 'react';
import { useGed } from '@/lib/store';
import { fetchEcriture } from '@/lib/ecrire';
import {
  I, card, inputStyle, TitrePage, Modale, EmptyState, Skeleton, enChargement, useConfirmation,
} from '@/components/ui';
import { Bouton } from '@/shared/Bouton';
import { PiedActions } from '@/shared/PiedActions';
import type { GedChecklistPoste, ChecklistEcarts, ChecklistRubrique } from '@/lib/types';
/* Les tâches en colonnes (RQ, 05/10/2026) — MÊME module que le mémo du jour :
   une liste de 73 points doit se lire pareil là où on la paramètre et là où on
   la suit. Les pièges (case coupée de son libellé, écran étroit) sont traités
   une fois pour les deux. */
import { styleColonnes, STYLE_TACHE_COLONNE } from '@/lib/colonnes-taches';

const lbl: React.CSSProperties = {
  display: 'block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
  letterSpacing: '0.04em', color: 'var(--text-muted)', marginBottom: 4,
};

type Reponse = { checklists?: GedChecklistPoste[]; ecarts?: ChecklistEcarts };

export default function Checklists() {
  const { rhPostes, session, loadRh } = useGed();
  const peutModifier = !!session.sections?.postes?.modifier;
  const [etat, setEtat] = React.useState<Reponse | null>(null);
  const [rev, setRev] = React.useState(0);
  const [edite, setEdite] = React.useState<GedChecklistPoste | 'nouvelle' | null>(null);
  /* ⚠️ LE MOTIF SE DEMANDE PAR `demanderTexte`, pas par une prop de bouton :
     `ConfirmButton` ne sait pas recueillir un texte, et le serveur EXIGE un
     motif de 5 caractères. Sans lui, la suppression serait refusée et l'écran
     n'aurait rien à montrer. C'est l'outil que les conventions prescrivent. */
  const { demanderTexte, confirmation } = useConfirmation();

  React.useEffect(() => {
    let vivant = true;
    void fetch('/api/ged/ged/rh/checklists', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { checklists: [] }))
      .catch(() => ({ checklists: [] }))
      .then((d: Reponse) => { if (vivant) setEtat(d); });
    return () => { vivant = false; };
  }, [rev]);
  /* Les postes servent à nommer les codes rattachés et à peupler le sélecteur.
     ⚠️ `rhPostes.length` N'EST PAS en dépendance, et volontairement : l'y mettre
     relancerait `loadRh` à chaque fois que la liste change, donc en boucle. On
     ne veut qu'un appel, si le magasin est vide à l'arrivée. */
  const postesVides = rhPostes.length === 0;
  React.useEffect(() => { if (postesVides) void loadRh(); }, [postesVides, loadRh]);

  const listes = etat?.checklists || [];
  const ec = etat?.ecarts;
  const attend = enChargement(etat !== null, false, listes.length);
  const libellePoste = (code: string) => rhPostes.find((p) => p.code === code)?.libelle || code;

  return (
    <section style={{ display: 'grid', gap: 16 }}>
      <TitrePage eyebrow="Postes de travail" titre="Check-lists de poste"
        sousTitre={<>Ce que la personne qui tient le poste passe en revue. Chaque liste s’affiche au
          mémo du jour, en haut du tableau de bord, pour qui tient l’un de ses postes.</>} />

      {/* ⚠️ LA RÉSERVE EN HAUT, PAS EN BAS. Une liste cochable ressemble à une
          preuve ; celui qui paramètre doit le savoir avant de saisir. */}
      <p role="note" style={{ margin: 0, padding: '10px 13px', borderRadius: 'var(--radius-md)', background: 'var(--amber-50)', color: 'var(--amber-600)', fontSize: 12.5, lineHeight: 1.5 }}>
        <strong>Ces listes ne sont pas un enregistrement.</strong> Cocher une case ne laisse aucune
        trace : les cases vivent dans le navigateur de la personne et se remettent à zéro chaque
        jour. La grille papier reste la seule preuve opposable. C’est un pense-bête — utile, mais
        qui n’atteste rien.
      </p>

      {/* ── LES ÉCARTS, EN PREMIER ──────────────────────────────────────────
          Ils expliquent une liste qui ne s'affiche plus. Calculés par le
          serveur : l'écran ne refait pas le raisonnement. */}
      {ec && (ec.postesInconnus.length > 0 || ec.appelsInconnus.length > 0
        || ec.postesEnDouble.length > 0 || ec.boucles.length > 0) && (
        <div role="alert" style={{ ...card, padding: 14, borderColor: 'var(--red-500)', display: 'grid', gap: 7 }}>
          <strong style={{ fontSize: 13.5, color: 'var(--red-600)' }}>À corriger — ces écarts empêchent une liste de s’afficher</strong>
          {ec.postesInconnus.map((x) => (
            <span key={`pi${x.code}${x.poste}`} style={{ fontSize: 12.5, lineHeight: 1.45 }}>
              <strong>{x.code}</strong> cite le poste <strong>{x.poste}</strong>, qui n’existe pas —
              elle ne s’affiche à personne pour ce poste. Poste renommé ou supprimé ?
            </span>
          ))}
          {ec.postesEnDouble.map((x) => (
            <span key={`pd${x.poste}`} style={{ fontSize: 12.5, lineHeight: 1.45 }}>
              Le poste <strong>{x.poste}</strong> est cité par <strong>{x.codes.join(' et ')}</strong> :
              une seule s’affichera, l’autre reste invisible.
            </span>
          ))}
          {ec.appelsInconnus.map((x) => (
            <span key={`ai${x.code}${x.appelle}`} style={{ fontSize: 12.5, lineHeight: 1.45 }}>
              <strong>{x.code}</strong> appelle <strong>{x.appelle}</strong>, qui n’existe pas : cet
              appel ne montre rien.
            </span>
          ))}
          {ec.boucles.length > 0 && (
            <span style={{ fontSize: 12.5, lineHeight: 1.45 }}>
              Boucle d’appels : <strong>{ec.boucles.join(', ')}</strong>. Le mémo tournerait en rond.
            </span>
          )}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          {listes.length} liste(s) · {listes.reduce((n, c) => n + c.nbTaches, 0)} tâche(s)
        </span>
        <span style={{ flex: 1 }} />
        {peutModifier && (
          <Bouton variante="primaire" onClick={() => setEdite('nouvelle')}>
            <I n="plus" s={13} />Créer une check-list
          </Bouton>
        )}
      </div>

      {attend ? <div style={{ ...card, padding: 0, overflow: 'hidden' }}><Skeleton rows={4} /></div>
        : !listes.length ? (
          <EmptyState titre="Aucune check-list enregistrée."
            indice="Les quatre listes du laboratoire (garde, réception, prélèvement, bactériologie) sont reprises au premier démarrage du moteur. Si cet écran est vide, c’est que la reprise n’a pas encore eu lieu." />
        ) : listes.map((c) => (
          <article key={c.documentId} style={{ ...card, padding: 14, display: 'grid', gap: 8, opacity: c.actif ? 1 : 0.6 }}>
            <div style={{ display: 'flex', gap: 9, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <strong style={{ fontSize: 14 }}>{c.libelle}</strong>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--text-muted)' }}>{c.code}</span>
              <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                {c.rubriques.length} rubrique(s) · {c.nbTaches} tâche(s)
              </span>
              {!c.actif && (
                <span style={{ fontSize: 11, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 9px', background: 'var(--gray-25)', color: 'var(--text-muted)' }}>
                  inactive — ne s’affiche pas au mémo
                </span>
              )}
              <span style={{ flex: 1 }} />
              {peutModifier && (
                <>
                  <Bouton taille="sm" onClick={() => setEdite(c)}><I n="pen" s={12} />Modifier</Bouton>
                  <Bouton taille="sm" variante="danger" aria-label={`Supprimer ${c.code}`}
                    onClick={async () => {
                      const motif = await demanderTexte(
                        `Supprimer la check-list « ${c.libelle} » et ses ${c.nbTaches} tâche(s) ? `
                        + 'Pour la retirer du mémo sans la perdre, décochez « active » à la place. '
                        + 'Motif de la suppression :',
                        { min: 5, confirmer: 'Supprimer', danger: true },
                      );
                      if (!motif) return;
                      await fetchEcriture(`/api/ged/ged/rh/checklists/${encodeURIComponent(c.code)}/supprimer`, {
                        method: 'POST', credentials: 'include',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ motif }),
                      });
                      setRev((n) => n + 1);
                    }}>
                    <I n="trash" s={12} />Supprimer
                  </Bouton>
                </>
              )}
            </div>

            {/* LES POSTES RATTACHÉS. ⚠️ « Aucun » SE DIT : une liste sans poste
                ne s'affiche à personne, et c'est une erreur de paramétrage, pas
                un état normal. */}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>Postes :</span>
              {!c.postes.length ? (
                <span style={{ fontSize: 12.5, color: 'var(--amber-600)', fontWeight: 600 }}>
                  aucun — cette liste ne s’affiche à personne
                </span>
              ) : c.postes.map((p) => (
                <span key={p} title={libellePoste(p)}
                  style={{ fontSize: 11.5, fontWeight: 600, borderRadius: 'var(--radius-pill)', padding: '2px 9px', background: 'var(--cyan-50)', color: 'var(--cyan-700)' }}>
                  {p}
                </span>
              ))}
            </div>

            {c.appelle.length > 0 && (
              <span style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.45 }}>
                Appelle aussi : <strong>{c.appelle.join(', ')}</strong> — leurs tâches s’ajoutent au
                mémo de ces postes.
              </span>
            )}
            {c.source && (
              <span style={{ fontSize: 12, color: 'var(--text-faint)' }}>
                Transcrite de : {c.source}
              </span>
            )}

            {/* Les rubriques, dépliables : 73 tâches dépliées d'office
                rendraient la page illisible. */}
            <details>
              <summary style={{ cursor: 'pointer', fontSize: 12.5, color: 'var(--color-primary-active)', fontWeight: 600 }}>
                Voir les {c.nbTaches} tâche(s)
              </summary>
              <div style={{ display: 'grid', gap: 10, marginTop: 8 }}>
                {c.rubriques.map((r, i) => (
                  <div key={i}>
                    <div style={{ ...lbl, marginBottom: 3 }}>{r.titre}</div>
                    {/* ⚠️ La NUMÉROTATION est conservée (`<ol>`) : les listes de
                        garde sont numérotées 1 → 31 dans le document source, et
                        les colonnes CSS remplissent de haut en bas puis à
                        droite — la suite se lit donc toujours dans l'ordre.
                        `paddingInlineStart` reste nécessaire : sans lui les
                        numéros débordent hors de leur colonne. */}
                    <ol style={{ margin: 0, paddingInlineStart: 22, fontSize: 12.5, lineHeight: 1.6, ...styleColonnes(r.taches.length) }}>
                      {r.taches.map((t, j) => <li key={j} style={STYLE_TACHE_COLONNE}>{t}</li>)}
                    </ol>
                  </div>
                ))}
              </div>
            </details>
          </article>
        ))}

      {/* LES POSTES SANS LISTE — un fait, pas un reproche : tous les postes
          n'ont pas de document source. Mais la RQ doit pouvoir le voir. */}
      {ec && ec.postesSansListe.length > 0 && (
        <div style={{ ...card, padding: 14 }}>
          <strong style={{ fontSize: 13 }}>Postes actifs sans check-list ({ec.postesSansListe.length})</strong>
          <p style={{ margin: '6px 0 8px', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
            Leur mémo du jour n’affiche aucune liste. Ce n’est pas forcément un manque — tous les
            postes n’ont pas de document de référence.
          </p>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {ec.postesSansListe.map((p) => (
              <span key={p.code} title={p.libelle}
                style={{ fontSize: 11.5, borderRadius: 'var(--radius-pill)', padding: '2px 9px', background: 'var(--gray-25)', color: 'var(--text-muted)' }}>
                {p.code}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* ⚠️ SANS CE RENDU, LA FENÊTRE DU MOTIF N'APPARAÎT JAMAIS et le clic ne
          fait rien — piège documenté du hook. */}
      {confirmation}

      {edite && (
        <FormulaireChecklist
          initial={edite === 'nouvelle' ? null : edite}
          postesConnus={rhPostes.filter((p) => p.actif !== false).map((p) => ({ code: p.code, libelle: p.libelle }))}
          autresCodes={listes.map((c) => c.code).filter((x) => edite === 'nouvelle' || x !== edite.code)}
          onFermer={() => setEdite(null)}
          onEnregistre={() => { setEdite(null); setRev((n) => n + 1); }} />
      )}
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 * LE FORMULAIRE
 * ──────────────────────────────────────────────────────────────────────────
 * ⚠️ LES TÂCHES SE SAISISSENT EN TEXTE, UNE PAR LIGNE, et c'est un choix : un
 * champ par tâche aurait donné soixante-treize champs pour la liste de
 * prélèvement, impossible à relire et pénible à réordonner. Coller depuis le
 * document Word devient aussi immédiat — c'est de là que viennent ces listes.
 * ═════════════════════════════════════════════════════════════════════════ */
function FormulaireChecklist({ initial, postesConnus, autresCodes, onFermer, onEnregistre }: {
  initial: GedChecklistPoste | null;
  postesConnus: { code: string; libelle: string }[];
  autresCodes: string[];
  onFermer: () => void;
  onEnregistre: () => void;
}) {
  const nouvelle = !initial;
  const [code, setCode] = React.useState(initial?.code || '');
  const [libelle, setLibelle] = React.useState(initial?.libelle || '');
  const [source, setSource] = React.useState(initial?.source || '');
  const [postes, setPostes] = React.useState<string[]>(initial?.postes || []);
  const [appelle, setAppelle] = React.useState<string[]>(initial?.appelle || []);
  const [actif, setActif] = React.useState(initial?.actif !== false);
  const [note, setNote] = React.useState(initial?.note || '');
  const [rubriques, setRubriques] = React.useState<ChecklistRubrique[]>(
    initial?.rubriques?.length ? initial.rubriques : [{ titre: '', taches: [] }],
  );
  const [envoi, setEnvoi] = React.useState(false);
  const [erreur, setErreur] = React.useState('');

  const nbTaches = rubriques.reduce((n, r) => n + r.taches.filter((t) => t.trim()).length, 0);
  const basculer = (l: string[], v: string, set: (x: string[]) => void) =>
    set(l.includes(v) ? l.filter((x) => x !== v) : [...l, v]);

  const majRubrique = (i: number, patch: Partial<ChecklistRubrique>) =>
    setRubriques((rs) => rs.map((r, k) => (k === i ? { ...r, ...patch } : r)));

  const enregistrer = async () => {
    setEnvoi(true); setErreur('');
    try {
      /* ⚠️ `fetchEcriture` rend la MÊME `Response` que `fetch` — elle n'analyse
         pas le corps. Le motif du refus est dans `error.message` du corps, et
         c'est lui qu'il faut lire : le serveur refuse des choses précises (code
         mal formé, poste inconnu, boucle d'appels), et afficher « refusé » tout
         court ferait ressaisir à l'aveugle. */
      const r = await fetchEcriture('/api/ged/ged/rh/checklists', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: code.trim().toUpperCase(), libelle, source, postes, appelle, actif, note,
          rubriques: rubriques
            .map((x) => ({ titre: x.titre, taches: x.taches.filter((t) => t.trim()) }))
            .filter((x) => x.taches.length),
        }),
      });
      const corps = await r.json().catch(() => null) as { error?: { message?: string } } | null;
      if (!r.ok) throw new Error(corps?.error?.message || 'Enregistrement refusé');
      onEnregistre();
    } catch (e) {
      /* ⚠️ LE MOTIF DU REFUS RESTE À L'ÉCRAN, fenêtre ouverte : le serveur
         refuse des choses précises (code mal formé, poste inconnu, boucle
         d'appels), et un toast qui disparaît ferait ressaisir à l'aveugle. */
      setErreur(e instanceof Error ? e.message : 'Enregistrement refusé');
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <Modale titre={nouvelle ? 'Nouvelle check-list' : `Modifier ${initial!.code}`} largeur={720}
      onClose={onFermer}
      pied={<PiedActions taille="sm" annuler={{ onClick: onFermer, disabled: envoi }}
        principale={{
          libelle: 'Enregistrer', libelleChargement: 'Enregistrement…', chargement: envoi,
          disabled: envoi || !code.trim() || !libelle.trim() || !nbTaches,
          onClick: enregistrer,
        }} />}>
      {erreur && (
        <p role="alert" style={{ margin: '0 0 12px', padding: '8px 11px', borderRadius: 'var(--radius-md)', background: 'var(--red-50)', color: 'var(--red-600)', fontSize: 12.5, lineHeight: 1.45 }}>
          {erreur}
        </p>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10 }}>
        <label style={lbl}>Code
          <input style={{ ...inputStyle, width: '100%' }} value={code} disabled={!nouvelle}
            onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="CL-GARDE" />
        </label>
        <label style={lbl}>Intitulé
          <input style={{ ...inputStyle, width: '100%' }} value={libelle}
            onChange={(e) => setLibelle(e.target.value)} placeholder="Check-list de garde" />
        </label>
      </div>

      <label style={{ ...lbl, marginTop: 10 }}>Document source
        <input style={{ ...inputStyle, width: '100%' }} value={source}
          onChange={(e) => setSource(e.target.value)} placeholder="check list Garde.docx" />
      </label>
      <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.45 }}>
        D’où les tâches sont tirées. Ce n’est pas un lien vers la GED : ces fichiers ne sont pas des
        documents maîtrisés. Le garder permet de savoir quoi relire si une tâche est contestée.
      </p>

      <fieldset style={{ border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: 10, marginTop: 14 }}>
        <legend style={{ fontSize: 12.5, fontWeight: 700, padding: '0 6px' }}>Postes concernés</legend>
        <p style={{ margin: '0 0 8px', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.45 }}>
          La liste s’affichera au mémo du jour de qui tient l’un de ces postes.
          <strong> Aucun poste coché = elle ne s’affiche à personne.</strong>
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {postesConnus.map((p) => {
            const on = postes.includes(p.code);
            return (
              <button key={p.code} type="button" aria-pressed={on} title={p.libelle}
                onClick={() => basculer(postes, p.code, setPostes)}
                style={{
                  fontFamily: 'var(--font-sans)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
                  borderRadius: 'var(--radius-pill)', padding: '5px 11px',
                  border: `1px solid ${on ? 'var(--color-primary-active)' : 'var(--border-default)'}`,
                  background: on ? 'var(--cyan-50)' : 'var(--surface-card)',
                  color: on ? 'var(--color-primary-active)' : 'var(--text-muted)',
                }}>
                {p.code}
              </button>
            );
          })}
        </div>
      </fieldset>

      {autresCodes.length > 0 && (
        <fieldset style={{ border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: 10, marginTop: 14 }}>
          <legend style={{ fontSize: 12.5, fontWeight: 700, padding: '0 6px' }}>Appelle d’autres listes</legend>
          <p style={{ margin: '0 0 8px', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.45 }}>
            Leurs tâches s’ajoutent au mémo. C’est le cas de la garde, qui renvoie à la réception et
            au prélèvement. Une boucle (A appelle B qui appelle A) est refusée : le mémo tournerait
            en rond.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {autresCodes.map((x) => {
              const on = appelle.includes(x);
              return (
                <button key={x} type="button" aria-pressed={on}
                  onClick={() => basculer(appelle, x, setAppelle)}
                  style={{
                    fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    borderRadius: 'var(--radius-pill)', padding: '5px 11px',
                    border: `1px solid ${on ? 'var(--color-primary-active)' : 'var(--border-default)'}`,
                    background: on ? 'var(--cyan-50)' : 'var(--surface-card)',
                    color: on ? 'var(--color-primary-active)' : 'var(--text-muted)',
                  }}>
                  {x}
                </button>
              );
            })}
          </div>
        </fieldset>
      )}

      <fieldset style={{ border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: 10, marginTop: 14 }}>
        <legend style={{ fontSize: 12.5, fontWeight: 700, padding: '0 6px' }}>
          Rubriques et tâches — {nbTaches} tâche(s)
        </legend>
        <p style={{ margin: '0 0 8px', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.45 }}>
          <strong>Une tâche par ligne.</strong> Collez directement depuis le document : les lignes
          vides sont ignorées. Une rubrique sans aucune tâche n’est pas enregistrée.
        </p>
        {rubriques.map((r, i) => (
          <div key={i} style={{ border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', padding: 9, marginBottom: 8 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'end' }}>
              <label style={{ ...lbl, flex: 1 }}>Intitulé de la rubrique
                <input style={{ ...inputStyle, width: '100%' }} value={r.titre}
                  onChange={(e) => majRubrique(i, { titre: e.target.value })}
                  placeholder="À la prise de poste" />
              </label>
              <Bouton taille="sm" type="button" aria-label={`Retirer la rubrique ${r.titre || i + 1}`}
                disabled={rubriques.length === 1}
                onClick={() => setRubriques((rs) => rs.filter((_, k) => k !== i))}>×</Bouton>
            </div>
            <label style={{ ...lbl, marginTop: 8 }}>
              Tâches ({r.taches.filter((t) => t.trim()).length})
              <textarea rows={Math.min(14, Math.max(4, r.taches.length + 1))}
                style={{ ...inputStyle, width: '100%', minHeight: 70, fontFamily: 'var(--font-sans)', lineHeight: 1.55 }}
                value={r.taches.join('\n')}
                onChange={(e) => majRubrique(i, { taches: e.target.value.split('\n') })}
                placeholder={'Vérifier la température des réfrigérateurs\nContrôler le stock de tubes'} />
            </label>
          </div>
        ))}
        <Bouton taille="sm" type="button" onClick={() => setRubriques((rs) => [...rs, { titre: '', taches: [] }])}>
          <I n="plus" s={12} /> Ajouter une rubrique
        </Bouton>
      </fieldset>

      <label style={{ ...lbl, marginTop: 12 }}>Note interne
        <textarea rows={2} style={{ ...inputStyle, width: '100%', minHeight: 46 }} value={note}
          onChange={(e) => setNote(e.target.value)} />
      </label>

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 12.5 }}>
        <input type="checkbox" checked={actif} onChange={(e) => setActif(e.target.checked)} />
        Active — une liste inactive reste enregistrée mais ne s’affiche plus au mémo du jour
      </label>
    </Modale>
  );
}
